// utils/demoTranscript.ts
// Pas 1.2 — demo real fără cont. The demo asset (UI/public/demo/) is a
// TEMPORARY synthetic technical example (ffmpeg lavfi tone + hand-written
// demonstrative cues, marked [ASSET TEMPORAR]) until a rights-cleared
// recording replaces it (human gate, plan §10.5). Everything here is pure
// and dependency-free so the landing page stays light: the hero plays the
// real demo file with cues synced from the same SRT the editor opens.

export const DEMO_SOURCE_ID = 'demo-transcriptor';
export const DEMO_AUDIO_URL = '/demo/demo.mp3';
export const DEMO_SRT_URL = '/demo/demo.srt';
export const DEMO_FILE_NAME = 'demo.srt';

export interface DemoCue {
  start: number; // seconds
  end: number; // seconds
  text: string;
}

export function isDemoSourceId(sourceId: unknown): boolean {
  return sourceId === DEMO_SOURCE_ID;
}

function parseSrtTime(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2}):(\d{2})[.,](\d{1,3})$/.exec(value.trim());
  if (!match) return null;
  const [, h, m, s, ms] = match;
  return Number(h) * 3600 + Number(m) * 60 + Number(s) + Number(ms.padEnd(3, '0')) / 1000;
}

/** Minimal SRT parser for the known-good demo asset (the editor keeps using
 * its contract-aware loaders for real content). Invalid blocks are skipped. */
export function parseDemoSrt(srtText: string): DemoCue[] {
  if (typeof srtText !== 'string' || !srtText.trim()) return [];
  const cues: DemoCue[] = [];
  for (const block of srtText.replace(/\r\n/g, '\n').split(/\n\s*\n/)) {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    const timeLineIndex = lines.findIndex((line) => line.includes('-->'));
    if (timeLineIndex < 0) continue;
    const [rawStart, rawEnd] = lines[timeLineIndex].split('-->');
    const start = parseSrtTime(rawStart || '');
    const end = parseSrtTime((rawEnd || '').trim().split(/\s+/)[0] || '');
    const text = lines.slice(timeLineIndex + 1).join(' ').trim();
    if (start === null || end === null || end <= start || !text) continue;
    cues.push({ start, end, text });
  }
  return cues.sort((a, b) => a.start - b.start);
}

/** Cue active at `seconds`; between cues, the next cue (so the hero caption
 * never goes blank mid-file); null before the first / after the last cue. */
export function activeDemoCue(cues: DemoCue[], seconds: number): DemoCue | null {
  if (!Array.isArray(cues) || cues.length === 0 || !Number.isFinite(seconds)) return null;
  for (const cue of cues) {
    if (seconds >= cue.start && seconds < cue.end) return cue;
    if (seconds < cue.start) return cue;
  }
  return null;
}

/** TranscriptSource-shaped object for useTranscriptEditor.openDemoTranscript.
 * No element/transcriptId: nothing exists server-side, edits stay local. */
export function buildDemoSource(srtText: string, audioUrl: string = DEMO_AUDIO_URL) {
  return {
    sourceId: DEMO_SOURCE_ID,
    type: 'text' as const,
    data: srtText,
    language: 'ro',
    audioUrl,
    fileName: DEMO_FILE_NAME,
  };
}
